import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import Tooltip from '../addentities/Tooltip.js';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';
import AlertNotification from '../../components/AlertNotification.js';
import LinkStockInstancesModal from './LinkStockInstancesModal.js'; // ← NEW

export default function PlatformManageOrder() {
    const history = useHistory();

    const getInitialFormData = () => {
        const saved = sessionStorage.getItem('platformManageOrder_formData');
        return saved
            ? JSON.parse(saved)
            : {
                  settlement_price: '',
                  currency: 'SGD',
                  linked_stock_instance: '',
                  notes: '',
              };
    };

    const [formData, setFormData] = useState(getInitialFormData);
    const [errors, setErrors] = useState({ settlement_price: '' });
    const [touchedFields, setTouchedFields] = useState({ settlement_price: false });
    const [disabled, setDisabled] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [alert, setAlert] = useState(null);
    const [showItemAlert, setShowItemAlert] = useState(false);
    const [discardModalOpen, setDiscardModalOpen] = useState(false);

    // ── NEW: Link StockInstances modal state ──────────────────────────────────
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkedInstances, setLinkedInstances] = useState([]); // [{id, qty}]
    // ─────────────────────────────────────────────────────────────────────────

    const discardRef = useRef(null);

    useEffect(() => {
        sessionStorage.setItem('platformManageOrder_formData', JSON.stringify(formData));
    }, [formData]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (discardRef.current && !discardRef.current.contains(event.target)) {
                setDiscardModalOpen(false);
            }
        }
        if (discardModalOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [discardModalOpen]);

    const showAlert = (type, action, recordName) => {
        setAlert({ type, action, recordName });
        setShowItemAlert(true);
        setTimeout(() => setShowItemAlert(false), 5000);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (!isDirty) setIsDirty(true);
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouchedFields((prev) => ({ ...prev, [name]: true }));
        if (name === 'settlement_price' && !value) {
            setErrors((prev) => ({ ...prev, settlement_price: 'Settlement Price is required' }));
            return;
        }
        if (name === 'settlement_price' && value && Number(value) < 0) {
            setErrors((prev) => ({ ...prev, settlement_price: 'Settlement Price cannot be negative' }));
            return;
        }
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const nextErrors = { settlement_price: '' };
        if (!formData.settlement_price && formData.settlement_price !== 0) {
            nextErrors.settlement_price = 'Settlement Price is required';
        } else if (Number(formData.settlement_price) < 0) {
            nextErrors.settlement_price = 'Settlement Price cannot be negative';
        }
        return nextErrors;
    };

    const handleSave = async () => {
        setIsSaving(true);
        setDisabled(true);
        setTouchedFields({ settlement_price: true });

        const nextErrors = validateForm();
        setErrors(nextErrors);

        const hasErrors = Object.values(nextErrors).some(Boolean);
        if (hasErrors) {
            setDisabled(false);
            setIsSaving(false);
            showAlert('error', 'create', 'Manage Order');
            return;
        }

        try {
            setTimeout(() => {
                setDisabled(false);
                setIsSaving(false);
                setIsDirty(false);
                sessionStorage.removeItem('platformManageOrder_formData');
                showAlert('success', 'create', 'Manage Order');
            }, 600);
        } catch (error) {
            setDisabled(false);
            setIsSaving(false);
            showAlert('error', 'create', 'Manage Order');
        }
    };

    const handleDiscard = () => {
        if (!isDirty || isSaving) {
            sessionStorage.removeItem('platformManageOrder_formData');
            history.push('/platform-order');
            return;
        }
        setDiscardModalOpen(true);
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            sessionStorage.removeItem('platformManageOrder_formData');
            setIsDirty(false);
            setFormData({ settlement_price: '', currency: 'SGD', linked_stock_instance: '', notes: '' });
            history.push('/platform-order', {
                success: 'Manage Order Discarded Successfully',
                action: 'discard',
                refresh: true,
            });
        }
        setDiscardModalOpen(false);
    };

    const handleCancelDiscard = () => setDiscardModalOpen(false);

    const handleBlockNavigation = () => {
        if (isSaving || !isDirty) return false;
        setDiscardModalOpen(true);
        return true;
    };

    // ── Handle confirmed linked instances ────────────────────────────────────
    const handleLinkConfirm = (selected) => {
        // selected = [{id, qty}]
        setLinkedInstances(selected);
        if (!isDirty) setIsDirty(true);
        setLinkModalOpen(false);
    };
    // ─────────────────────────────────────────────────────────────────────────

    const breadcrumbs = ['Order - Platform', 'Shopee SG', 'Manage Order'];

    return (
        <>
            <div
                className="company-body main-company-body-inner new-look"
                style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
            >
                {showItemAlert && alert && (
                    <AlertNotification
                        type={alert.type}
                        action={alert.action}
                        recordName={alert.recordName}
                        onClose={() => setShowItemAlert(false)}
                    />
                )}

                {/* Breadcrumb */}
                <div className="breadcrumb">
                    {breadcrumbs.map((crumb, index) => {
                        const isLast = index === breadcrumbs.length - 1;
                        return (
                            <span key={index} className="breadcrumb-item">
                                {crumb}{!isLast ? ' ' : ''}
                            </span>
                        );
                    })}
                </div>

                {/* Header */}
                <div className='edit-organisation-info'>
                    <div className='edit-organisation-left'>
                        <h1>Manage Order</h1>
                    </div>
                    <div className='discard-save-organisation-right d-flex align-items-center gap-2'>
                        <button
                            className="dark-button"
                            type='button'
                            onClick={handleDiscard}
                            style={{ width: '100px', padding: '8px', height: '34px' }}
                        >
                            Discard
                        </button>
                        <DiscardConfirmationModal
                            isOpen={discardModalOpen}
                            onCancel={handleCancelDiscard}
                            onConfirm={handleDiscardConfirm}
                            entityName="Manage Order"
                        />
                        <button
                            className="dark-border"
                            type='button'
                            onClick={handleSave}
                            disabled={disabled}
                            style={{ width: '100px', padding: '8px', height: '34px' }}
                        >
                            Save
                        </button>
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '24px' }}>
                    <div className="info-section">

                        {/* ── Prices card ── */}
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header">
                                <h4 className="info-title">Prices</h4>
                            </div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>
                                    <div className='row gappingcls-added'>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label
                                                    htmlFor="settlementPrice"
                                                    className="form-label d-flex align-items-center gap-2"
                                                >
                                                    Settlement Price <span className='error-color'>*</span>
                                                    <Tooltip
                                                        title="Settlement Price"
                                                        body="The settlement amount for the selected platform order."
                                                    >
                                                        <Info size={16} className="info-icon" />
                                                    </Tooltip>
                                                </label>

                                                <div
                                                    className={`form-control common-input-all d-flex align-items-center p-0 ${errors.settlement_price ? 'err-color' : ''}`}
                                                    style={{ overflow: 'hidden', height: '39px' }}
                                                >
                                                    <div
                                                        style={{
                                                            minWidth: '52px',
                                                            background: '#F3F4F6',
                                                            borderRight: '1px solid #CED4DA',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: '#6C757D',
                                                            fontSize: '14px',
                                                            height: '100%',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {formData.currency}
                                                    </div>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        id="settlementPrice"
                                                        name="settlement_price"
                                                        value={formData.settlement_price}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="0.00"
                                                        style={{
                                                            flex: 1,
                                                            border: 'none',
                                                            outline: 'none',
                                                            background: 'transparent',
                                                            padding: '0 10px',
                                                            fontSize: '14px',
                                                            height: '100%',
                                                        }}
                                                    />
                                                </div>
                                                {errors.settlement_price && (
                                                    <p style={{ color: '#DC3545', fontSize: '12px', marginTop: '4px', marginBottom: 0 }}>
                                                        {errors.settlement_price}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Linked StockInstances card ── */}
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header">
                                <h4 className="info-title">Linked StockInstances</h4>
                            </div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>
                                    <div className='row gappingcls-added'>
                                        <div className='col-12'>
                                            <div className="info-inner">
                                                <button
                                                    type="button"
                                                    className="dark-button"
                                                    onClick={() => setLinkModalOpen(true)}   // ← UPDATED
                                                    style={{
                                                        minWidth: '100px',
                                                        padding: '8px',
                                                        height: '34px',
                                                        backgroundColor: 'white',
                                                        color: '#212529',
                                                        border: '1px solid #212529',
                                                        borderRadius: '6px',
                                                        fontSize: '14px',
                                                        fontFamily: 'Montserrat',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    Link StockInstances
                                                </button>

                                                {/* Show summary of linked instances */}
                                                {linkedInstances.length > 0 && (
                                                    <p style={{ marginTop: 8, fontSize: 13, color: '#212529' }}>
                                                        {linkedInstances.length} stock instance{linkedInstances.length > 1 ? 's' : ''} linked
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!discardModalOpen && (
                            <UnsavedChangesPrompt
                                isDirty={isDirty && !isSaving}
                                when={isDirty && !isSaving}
                                entityName={'Manage Order'}
                                navigate={handleBlockNavigation}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Link StockInstances Modal ── */}
            <LinkStockInstancesModal
                isOpen={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                onConfirm={handleLinkConfirm}
            />
        </>
    );
}
